import React from 'react';
import { Box, LinearProgress, Stack, Typography } from '@mui/material';
import { FitbitExtractionJob } from '../../../services/fitbitExtractionService';

export function extractionStatus(job: FitbitExtractionJob) {
  if (job.kind === 'batch' && job.state === 'failed' && ((job.completedCount || 0) > 0 || (job.missingCount || 0) > 0)) return 'Needs attention';
  if (job.state === 'running') return job.progress?.phase === 'saving' ? 'Saving file' : job.progress?.phase === 'preparing' ? 'Preparing' : 'Extracting';
  return { queued: 'Queued', completed: 'Ready', failed: 'Failed', deleting: 'Deletion pending' }[job.state];
}

export function extractionFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes.toLocaleString()} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
}

export default function FitbitExtractionProgress({ job }: { job: FitbitExtractionJob }) {
  if (job.state === 'deleting') return null;
  const percentage = job.state === 'completed' ? 100 : job.state === 'queued' && job.kind !== 'batch' ? 0 :
    job.progress && Number.isFinite(job.progress.percentage) ? Math.max(0, Math.min(99, Math.floor(job.progress.percentage))) : null;
  const label = job.kind === 'batch' ? `${job.completedCount || 0} of ${job.participantCount} files ready` : job.state === 'completed' ? 'File ready' : job.state === 'failed' ? 'Extraction stopped' :
    job.state === 'queued' ? 'Waiting to start' : job.progress?.phase === 'saving' ? 'Saving to Google Cloud' :
      job.progress?.phase === 'preparing' ? 'Preparing extraction' : 'Date range processed';
  return <Box sx={{ mt: 1.25 }}>
    <Stack direction="row" justifyContent="space-between" gap={1} sx={{ mb: .5 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="caption" fontWeight={650}>{percentage === null ? 'Progress unavailable' : `${percentage}%`}</Typography>
    </Stack>
    {(percentage !== null || job.state === 'running') && <LinearProgress
      aria-label={`${job.name} extraction progress`} variant={percentage === null ? 'indeterminate' : 'determinate'} value={percentage ?? undefined}
      color={job.state === 'failed' ? 'error' : job.state === 'completed' ? 'success' : 'primary'} sx={{ height: 6, borderRadius: 3 }} />}
  </Box>;
}
